import type { DcrhcoInput, Hco } from '@/core/graphql/generated/types';

import type { useDcrSetup } from '../../_hooks/useDcrSetup';
import type { usePicklistValues } from '../../_hooks/usePicklistValues';
import type { HcoDcrValuesIn } from '../../_schemas/hcoSchema';
import type { mapHcoToFormDefaultsIn } from '../mapHcoToFormDefaultsIn';

import { getNetworkFieldValue } from './getNetworkFieldValue';
import { mapAddressesToNetworkApi } from './mapAddressToNetworkApi';
import { mapAffiliationToNetworkApi } from './mapAffiliationToNetworkApi';
import { resolvePicklistId } from './resolvePicklistId';

type FormDefaults = ReturnType<typeof mapHcoToFormDefaultsIn>;

type SpecialtyItem = {
	vid_V?: string;
	id?: string;
	isPrimary?: boolean;
	fieldName?: string;
};

type Props = {
	payload: Partial<FormDefaults>;
	vid_V: Hco['vid_V'] | undefined;
	countryId: string | undefined;
	picklists: ReturnType<typeof usePicklistValues>['picklists'];
	hco: Partial<Hco> | null;
	unavailableFields: ReturnType<typeof useDcrSetup>['unavailableFields'];
};

export const DCR_CUSTOM_HCO_FIELDS: Partial<
	Record<keyof Hco, keyof HcoDcrValuesIn>
> = {};

export const mapHcoToNetworkApiFieldNames = ({
	payload,
	vid_V,
	countryId,
	hco: _hco,
	unavailableFields,
	picklists: {
		specialtyLocalOptions,
		hcoTypeOptions,
		localHcoTypeOptions,
		hcoHierarchyTypeOptions,
		countRangeOptions,
		hcoOwnershipOptions,
		academicStatusOptions,
		statusOptions,
		relationshipTypeOptions,
		relationStatusOptions,
		countryOptions,
		stateOptions,
		hcoBusinessTypeOptions
	}
}: Props): Partial<DcrhcoInput> => {
	const hcoType = payload?.hcoType;
	const resolvedHcoType = hcoTypeOptions?.find(
		ho => ho.name === hcoType || ho.id === hcoType
	);

	const hcoTypeLocal = payload?.hcoTypeLocal_VId;
	const resolvedHcoTypeLocal = localHcoTypeOptions?.find(
		ho => ho.name === hcoTypeLocal || ho.id === hcoTypeLocal
	);

	const typeOfHierarchy = payload?.typeOfHierarchy_VId;
	const resolvedTypeOfHierarchy = hcoHierarchyTypeOptions?.find(
		ho => ho.name === typeOfHierarchy || ho.id === typeOfHierarchy
	);

	const countHcp = payload?.countHcp_VId;
	const resolvedCountHcp = countRangeOptions?.find(
		ho => ho.name === countHcp || ho.id === countHcp
	);

	const countPrescriber = payload?.countPrescriber_VId;
	const resolvedCountPrescriber = countRangeOptions?.find(
		ho => ho.name === countPrescriber || ho.id === countPrescriber
	);

	const hcoOwnership = payload?.hcoOwnership_VId;
	const resolvedHcoOwnership = hcoOwnershipOptions?.find(
		ho => ho.name === hcoOwnership || ho.id === hcoOwnership
	);

	const academicStatus = payload?.academicStatus_VId;
	const resolvedAcademicStatus = academicStatusOptions?.find(
		ho => ho.name === academicStatus || ho.id === academicStatus
	);

	const status = payload?.statusCda_V;
	const resolvedStatus = statusOptions?.find(
		s => s.id === status || s.name === status
	);

	const ownership = payload?.businessType_V;
	const resolvedOwnership = hcoBusinessTypeOptions?.find(
		o => o.id === ownership || o.name === ownership
	);

	// Helper: Resolve ID
	const getId = (s: SpecialtyItem) => {
		const rawValue = s.id ?? s.vid_V;
		if (!rawValue) return undefined;
		const option = specialtyLocalOptions?.find(
			spec => spec.id === rawValue || spec.name === rawValue
		);
		return option?.id ?? rawValue;
	};

	const mappedValues = {
		// Meta fields
		vid_V: vid_V,

		// Name Section
		hcoNameCda_V: getNetworkFieldValue(payload.hcoNameCda_V),

		// Organizational Section
		hcoTypeCda_VId: resolvePicklistId(hcoType, resolvedHcoType?.id),
		hcoTypeLocal_VId: resolvePicklistId(hcoTypeLocal, resolvedHcoTypeLocal?.id),
		typeOfHierarchy_VId: resolvePicklistId(
			typeOfHierarchy,
			resolvedTypeOfHierarchy?.id
		),
		countHcp_VId: resolvePicklistId(countHcp, resolvedCountHcp?.id),
		countPrescriber_VId: resolvePicklistId(
			countPrescriber,
			resolvedCountPrescriber?.id
		),
		statusCda_VId: resolvePicklistId(status, resolvedStatus?.id),
		nhidCda_V: getNetworkFieldValue(payload?.nhidCda_V),
		hcoTaxId_V: getNetworkFieldValue(payload.hcoTaxId_V),
		hcoOwnership_VId: resolvePicklistId(hcoOwnership, resolvedHcoOwnership?.id),
		academicStatus_VId: resolvePicklistId(
			academicStatus,
			resolvedAcademicStatus?.id
		),
		businessType_VId: resolvePicklistId(ownership, resolvedOwnership?.id),
		// vaDodAffiliated_V:
		// 	parseYesNoBoolean(payload.vaDodAffiliated_V) ?? undefined,

		allSpecLocal_VId:
			'allSpecLocal_VId' in payload
				? (payload.allSpecLocal_VId
						?.map(s => getId(s))
						.filter((id): id is string => !!id) ?? [])
				: undefined,

		// Contact Section
		emailCda_V: getNetworkFieldValue(payload.emailCda_V),
		// faxCda_V: payload.faxCda_V ?? undefined,
		// officePhoneCda_V: payload.officePhoneCda_V ?? undefined,
		websiteCda_V: getNetworkFieldValue(payload.websiteCda_V),

		// Nested entities
		addresses_V: payload.addresses
			? mapAddressesToNetworkApi({
					addresses: payload.addresses,
					countryOptions,
					stateOptions,
					statusOptions,
					unavailableFields
				})
			: undefined,

		parentHcos_V: payload.parenthcos
			? mapAffiliationToNetworkApi({
					affiliations: payload.parenthcos,
					relationshipTypeOptions,
					relationStatusOptions,
					unavailableFields
				})
			: undefined,

		// Unused fields
		licenses_V: undefined,
		primaryCountry_V: undefined
	};

	const map =
		vid_V !== undefined
			? { ...mappedValues, vid_V }
			: { ...mappedValues, primaryCountry_V: countryId };

	type MapType = typeof map;
	type Key = keyof MapType;

	const keys = Object.keys(map) as Key[];

	const result = keys.reduce((acc, key) => {
		if (unavailableFields?.includes(key as string)) {
			return acc;
		}

		const value = map[key];
		if (value !== undefined) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(acc as any)[key] = value;
		}
		return acc;
	}, {} as Partial<MapType>);

	return result as Partial<DcrhcoInput>;
};
